import DOMPurify from 'dompurify';

export function htmlToPlainTextWeb(html = '') {
  if (!html || !html.trim()) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="root">${html}</div>`,
    'text/html'
  );
  const root = doc.getElementById('root');
  if (!root) return '';

  const blockTags = new Set([
    'P',
    'DIV',
    'LI',
    'BLOCKQUOTE',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
    'PRE',
  ]);

  const lines = [];

  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = (node.textContent || '').replace(/\u00A0/g, ' ');
      if (text) {
        lines.push(text);
      }
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;

    if (el.tagName === 'BR') {
      lines.push('\n');
      return;
    }

    if (el.tagName === 'LI') {
      lines.push('• ');
    }

    for (const child of el.childNodes) {
      walk(child);
    }

    if (blockTags.has(el.tagName)) {
      lines.push('\n');
    }
  };

  walk(root);

  return lines
    .join('')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]*\n[ \t]*\n+/g, '\n\n')
    .replace(/[ \t]{2,}/g, ' ')
    .trim();
}

export function sanitizeDetailHtmlWeb(html = '') {
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'div',
      'span',
      'strong',
      'b',
      'em',
      'i',
      'u',
      's',
      'ul',
      'ol',
      'li',
      'blockquote',
      'pre',
      'code',
      'a',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'table',
      'thead',
      'tbody',
      'tfoot',
      'tr',
      'th',
      'td',
      'caption',
      'colgroup',
      'col',
    ],
    ALLOWED_ATTR: [
      'href',
      'target',
      'rel',
      'class',
      'colspan',
      'rowspan',
      'scope',
    ],
    FORBID_ATTR: ['style'],
  });
}

export function normalizeDetailHtmlWeb(html = '') {
  if (!html || !html.trim()) return '';

  const sanitized = sanitizeDetailHtmlWeb(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(
    `<div id="root">${sanitized}</div>`,
    'text/html'
  );
  const root = doc.getElementById('root');

  if (!root) return sanitized.trim();

  const isCodeLike = (el) => !!el.closest('pre, code');

  const hasMeaningfulChildren = (el) =>
    !!el.querySelector(
      'img, video, iframe, table, pre, code, ul, ol, li, blockquote'
    );

  const textWithoutBr = (el) => {
    const clone = el.cloneNode(true);
    clone.querySelectorAll('br').forEach((br) => br.remove());
    return (clone.textContent || '').replace(/\u00A0/g, ' ').trim();
  };

  const isEmptyBlock = (el) => {
    if (!el || isCodeLike(el)) return false;
    if (hasMeaningfulChildren(el)) return false;
    return textWithoutBr(el) === '';
  };

  const isIgnorableLeadingNode = (node) => {
    if (!node) return true;

    if (node.nodeType === Node.TEXT_NODE) {
      return !(node.textContent || '').replace(/\u00A0/g, ' ').trim();
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return true;

    const el = node;

    if (el.tagName === 'BR') return true;

    if (el.tagName === 'SPAN') {
      const txt = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
      return !txt && !el.querySelector('img, a, pre, code, ul, ol, li');
    }

    return false;
  };

  const getNodeVisibleText = (node) => {
    if (!node) return '';

    if (node.nodeType === Node.TEXT_NODE) {
      return (node.textContent || '').replace(/\u00A0/g, ' ');
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = node;

    if (el.tagName === 'BR') return '\n';

    let out = '';
    for (const child of el.childNodes) {
      out += getNodeVisibleText(child);
    }
    return out;
  };

  const unwrapQuotedFenceText = (text = '') => {
    let s = String(text)
      .replace(/\u00A0/g, ' ')
      .replace(/\r\n?/g, '\n')
      .trim();

    const pairs = [
      ['"', '"'],
      ["'", "'"],
      ['“', '”'],
      ['‘', '’'],
    ];

    for (const [open, close] of pairs) {
      if (s.startsWith(open) && s.endsWith(close)) {
        s = s.slice(1, -1).trim();
        break;
      }
    }

    return s;
  };

  const parseLeadingFenceFromBlock = (blockEl) => {
    if (!blockEl || !['P', 'DIV'].includes(blockEl.tagName)) return null;
    if (isCodeLike(blockEl)) return null;
    if (
      blockEl.querySelector(
        'pre, code, ul, ol, li, blockquote, img, video, iframe, table'
      )
    )
      return null;

    const nodes = Array.from(blockEl.childNodes);
    let i = 0;

    while (i < nodes.length && isIgnorableLeadingNode(nodes[i])) i++;

    if (i >= nodes.length) return null;

    const firstText = unwrapQuotedFenceText(getNodeVisibleText(nodes[i]));
    if (!/^```/.test(firstText)) return null;

    let acc = '';
    let endIndex = -1;

    for (let j = i; j < nodes.length; j++) {
      acc += getNodeVisibleText(nodes[j]);
      const normalized = unwrapQuotedFenceText(acc);

      const m = normalized.match(/^```([^\n`]*)\n([\s\S]*?)\n```(?:\n|$)/);
      if (m) {
        endIndex = j;
        return {
          startIndex: i,
          endIndex,
          code: m[2].replace(/\n+$/g, ''),
        };
      }
    }

    return null;
  };

  const replaceLeadingFenceBlocks = () => {
    root.querySelectorAll('p, div').forEach((blockEl) => {
      const parsed = parseLeadingFenceFromBlock(blockEl);
      if (!parsed) return;

      const parent = blockEl.parentNode;
      if (!parent) return;

      const nodes = Array.from(blockEl.childNodes);
      const before = nodes.slice(0, parsed.startIndex);
      const fenceNodes = nodes.slice(parsed.startIndex, parsed.endIndex + 1);
      const after = nodes.slice(parsed.endIndex + 1);

      const beforeHasMeaningful = before.some((n) => {
        const t = getNodeVisibleText(n)
          .replace(/\u00A0/g, ' ')
          .trim();
        return !!t;
      });

      if (beforeHasMeaningful) return;

      const pre = doc.createElement('pre');
      const code = doc.createElement('code');
      code.textContent = parsed.code;
      pre.appendChild(code);

      const frag = doc.createDocumentFragment();
      frag.appendChild(pre);

      const remainder = doc.createElement(blockEl.tagName.toLowerCase());
      after.forEach((n) => remainder.appendChild(n.cloneNode(true)));

      const remainderText = (remainder.textContent || '')
        .replace(/\u00A0/g, ' ')
        .trim();

      if (
        remainderText ||
        remainder.querySelector('br, strong, b, em, i, u, s, a, span')
      ) {
        frag.appendChild(remainder);
      }

      blockEl.replaceWith(frag);
    });
  };

  // Remove noisy editor attrs like ChatGPT / editor bookkeeping
  root.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach((attr) => {
      if (attr.name.startsWith('data-')) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // NEW: split leading quoted fence text out of mixed paragraphs/divs
  replaceLeadingFenceBlocks();

  // Remove empty spans, but preserve anything inside code/pre or with meaningful children
  root.querySelectorAll('span').forEach((el) => {
    if (isCodeLike(el)) return;

    const text = (el.textContent || '').replace(/\u00A0/g, ' ').trim();
    if (!text && !el.querySelector('br, img, code, pre, a, ul, ol, li')) {
      el.remove();
    }
  });

  // Remove only repeated empty block separators, not every empty block
  root.querySelectorAll('p, div').forEach((el) => {
    if (!isEmptyBlock(el)) return;

    const prev = el.previousElementSibling;
    const next = el.nextElementSibling;

    const prevIsEmpty =
      prev &&
      (prev.tagName === 'P' || prev.tagName === 'DIV') &&
      isEmptyBlock(prev);

    const nextIsEmpty =
      next &&
      (next.tagName === 'P' || next.tagName === 'DIV') &&
      isEmptyBlock(next);

    if (!prev || !next || prevIsEmpty || nextIsEmpty) {
      el.remove();
    }
  });

  // Protect <pre> blocks before final cleanup
  const preBlocks = [];
  root.querySelectorAll('pre').forEach((pre, index) => {
    const token = `__MSDB_PRE_${index}__`;
    preBlocks.push({ token, html: pre.outerHTML });
    pre.replaceWith(doc.createTextNode(token));
  });

  let output = root.innerHTML;

  output = output
    .replace(/&nbsp;/gi, ' ')
    .replace(/(<br\s*\/?>\s*){3,}/gi, '<br><br>')
    .replace(/\s+<\/p>/gi, '</p>')
    .replace(/\s+<\/div>/gi, '</div>')
    .trim();

  // Restore pre blocks exactly
  preBlocks.forEach(({ token, html }) => {
    output = output.replace(token, html);
  });

  return output;
}
